import Panel from '@/components/panel/Panel.jsx';
import { PiFiles } from '@/components/react-icons/main';
import Select from '@/components/Select.jsx';
import buildInputStepObjects from '@/hooks/useWorkflowCustomEditor/buildInputStepObjects.js';
import buildMapEditorInput from '@/hooks/useWorkflowCustomEditor/buildMapEditorInput.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import selectors from '@/redux/selectors/selectors.js';
import argumentDataSources from '@/schema/argumentDataSources.js';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ArgumentLabel from '../ArgumentLabel.jsx';
import ArgumentWrapper from '../ArgumentWrapper.jsx';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';

const batchSizes = [
  { value: 1, label: '1 Record' },
  { value: 5, label: '5 Records' },
  { value: 10, label: '10 Records' },
  { value: 25, label: '25 Records' },
  { value: 50, label: '50 Records' },
  { value: 100, label: '100 Records' },
  { value: 250, label: '250 Records' },
  { value: 500, label: '500 Records' },
  { value: 1000, label: '1,000 Records' },
  { value: 2000, label: '2,000 Records' },
  { value: 4000, label: '4,000 Records' }
];

const BatchingSlider = ({ onClose, dataSource, dataSourcePath, dataSavePath, disabled = false }) => {
  const stepId = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.id);
  const inputStepIds = useSelector(memoizeSelector('inputStepIds', (state) => workflowScreenSelectors.activeWorkflowStep(state)?.arguments?.inputStepIds));
  const inputStepIdParents = useSelector(memoizeSelector('inputStepIdParents', (state) => workflowScreenSelectors.activeWorkflowStep(state)?.arguments?.inputStepIdParents));
  const options = useSelector(argumentSelectors[dataSource].activeOptions);
  const actions = dataSource === argumentDataSources.workflowStep ? stepActions : appActions;
  const value = options?.batching;

  const apps = useSelector(selectors.appsVersions);
  const stepOutputs = useSelector(selectors.cookedWorkflowStepOutputs);
  const steps = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.steps);
  const workflow = useSelector(workflowScreenSelectors.activeWorkflow);

  const inputSteps = useMemo(() => buildInputStepObjects({ inputStepIds, apps, steps, stepOutputs, inputStepIdParents }), [inputStepIds, apps, steps, stepOutputs, inputStepIdParents]);
  const { editorInput } = useMemo(() => buildMapEditorInput({ stepId, inputSteps, apps, workflow }), [inputSteps, stepId]);

  const actionStepsOptions = useMemo(() => {
    return (inputStepIds || [])
      .map((n) => {
        const output = inputSteps?.find((d) => d?.step?.id === n)?.output?.data;
        const indexOfOutput = Object.values(editorInput)?.findIndex((d) => JSON.stringify(d) === JSON.stringify(output));
        const key = Object.keys(editorInput)?.[indexOfOutput];
        const step = steps?.find((d) => d?.id === n);

        return {
          value: key,
          appSlug: step?.appSlug,
          label: `${step?.stepNumber}. ${step?.message || 'No Description'}`
        };
      })
      .filter((n) => !['appSelector', 'ifElse'].includes(n.appSlug));
  }, [inputStepIds, inputSteps, editorInput, steps]);

  const handleDelete = () => {
    actions.set(`${dataSavePath ? `${dataSavePath}.options` : dataSourcePath}`, (prev) => ({ ...prev, batching: null }));
    onClose();
  };

  const handleBatchingChange = (key, value) => {
    actions.set(`${dataSavePath ? `${dataSavePath}.options` : dataSourcePath}.batching`, (prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    setTimeout(() => {
      document.querySelector('.batching-slider .comp-input input')?.focus();
    }, 200);
  }, []);

  return (
    <>
      <SliderHeader disabled={disabled} showDelete showRun onClose={onClose} onDelete={handleDelete} />
      <SliderTitle //
        icon={PiFiles}
        title="Configure Batching"
        description="Batching helps process large datasets by splitting them into smaller chunks."
        helpIconContext="workflowStepBatchingContext"
      />
      <Panel.Section title="Settings" collapsable={false} helpIconContext="workflowStepBatchingContext">
        <Panel.Alert variant="warn" description="If enabled, sorting data or attempting to search the full input won't be possible." />
        <ArgumentWrapper>
          <ArgumentLabel className={'small-gap'} label={'Batch Size'} description={'How many records should we process at once?'} />
          <Select //
            disabled={disabled}
            placeholder={'Select batch size'}
            value={batchSizes.find((n) => n.value === value?.batchSize)?.label}
            onChange={({ value }) => handleBatchingChange('batchSize', value)}
            options={batchSizes}
            useSearch={false}
          />
        </ArgumentWrapper>
        {actionStepsOptions?.length > 1 && (
          <ArgumentWrapper>
            <ArgumentLabel className={'small-gap'} label={'Step to Batch'} description={"Which step's data should we batch?"} />
            <Select //
              disabled={disabled}
              placeholder={'Select a step'}
              value={actionStepsOptions.find((n) => n.value === value?.batchInputKey)?.label}
              onChange={({ value }) => handleBatchingChange('batchInputKey', value)}
              options={actionStepsOptions}
              useSearch={false}
            />
          </ArgumentWrapper>
        )}
      </Panel.Section>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default BatchingSlider;
